'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { 
  Sparkles, 
  Target,
  MessageSquare,
  BarChart3,
  Copy,
  Plus,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { VersionsDropdown, type Version } from '@/components/versions';
import { ArtifactGeneratorInput, type AttachedFile } from '@/components/shared/artifact-generator-input';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import {
  useDiscovery,
  useDiscoveryVersions,
  useCreateDiscoveryRow,
  useUpdateDiscoveryRow,
  useDeleteDiscoveryRow,
  useRestoreVersion,
  useGenerateDiscovery,
  useUpdateArtefactStatus,
  type DiscoveryType,
  type DiscoveryGoal,
  type DiscoveryInterview,
  type DiscoveryCompetitor,
  type DiscoveryVersion,
} from '@/hooks/use-discovery';
import { useAuthToken } from '@/hooks/use-auth';
import { EditRowDialog } from './edit-row-dialog';
import { VersionHistoryModal } from './version-history-modal';
import { RowDetailModal } from './row-detail-modal';
import { GoalDetailView } from './goal-detail-view';
import { InterviewDetailView } from './interview-detail-view';
import { CompetitorDetailView } from './competitor-detail-view';
import { AIGenerationProgress } from './ai-generation-progress';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';



interface DiscoveryConfig {
  id: DiscoveryType;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  emptyTitle: string;
  emptyDescription: string;
  placeholder: string;
  askAIPlaceholder: string;
}

const discoveryConfigs: Record<DiscoveryType, DiscoveryConfig> = {
  goals: {
    id: 'goals',
    title: 'Goals and Objectives',
    subtitle: 'Goals and objectives for',
    icon: Target,
    emptyTitle: 'Define Goals and Objectives',
    emptyDescription: 'Generate project goals and objectives by describing your business outcomes, user needs, and success metrics, or upload strategic documents and briefs.',
    placeholder: "Describe your project goals... (e.g., 'Increase user engagement by 20%, reduce churn rate, improve NPS score')",
    askAIPlaceholder: "E.g., Add KPIs for each goal, focus on measurable outcomes...",
  },
  interviews: {
    id: 'interviews',
    title: 'Stakeholder Interviews',
    subtitle: 'Stakeholder interviews for',
    icon: MessageSquare,
    emptyTitle: 'Document Stakeholder Interviews',
    emptyDescription: 'Generate stakeholder interview summaries by describing key stakeholders and their perspectives, or upload interview transcripts and meeting notes.',
    placeholder: "Describe your stakeholders... (e.g., 'VP of Product focused on revenue, Engineering Lead concerned about scale')",
    askAIPlaceholder: "E.g., Add more stakeholders, focus on technical requirements...",
  },
  competitors: {
    id: 'competitors',
    title: 'Competitor Analysis',
    subtitle: 'Competitive landscape analysis for',
    icon: BarChart3,
    emptyTitle: 'Analyze Competitors',
    emptyDescription: 'Generate comprehensive competitor analysis by describing your market and competitors, or upload market research reports and competitive intelligence.',
    placeholder: "Describe your competitive landscape... (e.g., 'Collaboration tools like Notion, Figma, Miro targeting enterprise teams')",
    askAIPlaceholder: "E.g., Add pricing comparison, focus on enterprise features...",
  },
};

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-green-100 text-green-700 border-green-200',
};

const categoryColors: Record<string, string> = {
  BUSINESS: 'bg-purple-100 text-purple-700',
  USER: 'bg-blue-100 text-blue-700',
  PRODUCT: 'bg-indigo-100 text-indigo-700',
  TECHNICAL: 'bg-slate-100 text-slate-700',
};

const threatColors: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-green-100 text-green-700',
};

const influenceColors: Record<string, string> = {
  HIGH: 'bg-purple-100 text-purple-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  LOW: 'bg-gray-100 text-gray-700',
};

interface DiscoveryPanelProps {
  projectId: string;
  projectName?: string;
  activeView?: DiscoveryType;
  onViewChange?: (view: DiscoveryType) => void;
  onAskAI?: (message: string) => void;
  onVersionSelect?: (version: Version) => void;
  onUndo?: () => void;
  onCopy?: () => void;
}

export function DiscoveryPanel({
  projectId,
  projectName = 'Enterprise Banking Platform',
  activeView = 'goals',
  onViewChange,
  onAskAI,
  onVersionSelect,
  onUndo,
  onCopy,
}: DiscoveryPanelProps) {
  const [showAskAI, setShowAskAI] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRowForDetail, setSelectedRowForDetail] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // Filters state
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [influenceFilter, setInfluenceFilter] = useState<string>('');
  const [threatFilter, setThreatFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Undo history (session-level)
  const undoHistoryRef = useRef<Array<{ action: string; data: any; timestamp: number }>>([]);
  
  // Generation state
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [lastGenerationParams, setLastGenerationParams] = useState<{ prompt: string; files: AttachedFile[] } | null>(null);
  
  // Track which version is currently active
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  
  const { toast } = useToast();
  const token = useAuthToken();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch discovery data
  const { data: discoveryData, isLoading, isError, error } = useDiscovery(projectId, activeView);
  const { data: versions } = useDiscoveryVersions(projectId, activeView);
  
  // Reset UI state when switching tabs
  useEffect(() => {
    setShowAskAI(false);
    setShowAddDialog(false);
    setShowDetailModal(false);
    setEditingRowId(null);
    setSelectedRowForDetail(null);
    setShowVersionHistory(false);
    setGenerationError(null);
    setLastGenerationParams(null);
    setCurrentVersionId(null); // Reset version tracking
    // Reset filters
    setPriorityFilter('');
    setCategoryFilter('');
    setInfluenceFilter('');
    setThreatFilter('');
    setShowFilters(false);
  }, [activeView]);

  // Deep link handling: Open row detail when ?item=XXX or ?row=XXX is in URL
  useEffect(() => {
    const itemId = searchParams.get('item') || searchParams.get('row');
    if (itemId && discoveryData?.items) {
      // Check if this item exists in the current data
      const itemExists = (discoveryData.items as { id: string }[]).some(item => item.id === itemId);
      if (itemExists) {
        setSelectedRowForDetail(itemId);
        // All types now use inline detail view
      }
    } else if (!itemId && selectedRowForDetail) {
      // URL param was removed (e.g., by browser back), close detail view
      setSelectedRowForDetail(null);
      setShowDetailModal(false);
    }
  }, [searchParams, discoveryData?.items, activeView]);

  // Mutations
  const createRow = useCreateDiscoveryRow(projectId, activeView);
  const updateRow = useUpdateDiscoveryRow(projectId, activeView);
  const deleteRow = useDeleteDiscoveryRow(projectId, activeView);
  const restoreVersion = useRestoreVersion(projectId, activeView);
  const generate = useGenerateDiscovery(projectId, activeView);
  const updateStatus = useUpdateArtefactStatus(projectId, activeView);
  
  // Status menu state
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const config = discoveryConfigs[activeView];
  const Icon = config.icon;

  const allItems = discoveryData?.items || [];
  
  // Apply filters
  const items = allItems.filter((item: any) => {
    if (activeView === 'goals') {
      const goal = item as DiscoveryGoal;
      if (priorityFilter && goal.priority !== priorityFilter) return false;
      if (categoryFilter && goal.category !== categoryFilter) return false;
    } else if (activeView === 'interviews') {
      const interview = item as DiscoveryInterview;
      if (influenceFilter && interview.influence !== influenceFilter) return false;
    } else if (activeView === 'competitors') {
      const competitor = item as DiscoveryCompetitor;
      if (threatFilter && competitor.threatLevel !== threatFilter) return false;
      if (categoryFilter && competitor.category !== categoryFilter) return false;
    }
    return true;
  });
  
  const hasContent = allItems.length > 0;
  const hasActiveFilters = priorityFilter || categoryFilter || influenceFilter || threatFilter;

  // Find the row being edited
  const editingRow = editingRowId ? items.find((item: any) => item.id === editingRowId) : null;

  // Initialize currentVersionId to the latest version when versions load
  useEffect(() => {
    if (versions?.length && !currentVersionId) {
      setCurrentVersionId(versions[0].id);
    }
  }, [versions, currentVersionId]);

  // Convert versions to Version type for dropdown
  const versionOptions: Version[] = (versions || []).map((v: DiscoveryVersion) => ({
    id: v.id,
    name: `Version ${v.version}`,
    isCurrent: currentVersionId ? v.id === currentVersionId : v.version === (versions?.[0]?.version || 1),
    timestamp: new Date(v.createdAt).toLocaleString(),
  }));

  const handleGenerate = async (description: string, attachedFiles: AttachedFile[]) => {
    // Save params for retry
    setLastGenerationParams({ prompt: description, files: attachedFiles });
    setGenerationError(null);
    
    try {
      await generate.mutateAsync({
        prompt: description,
        attachedFiles: attachedFiles.map(f => ({
          name: f.name,
          type: f.type,
          size: f.size,
        })),
      });
      setLastGenerationParams(null);
      toast({
        title: 'Content Generated',
        description: 'AI has generated your content successfully.',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate content';
      setGenerationError(errorMessage);
      toast({
        title: 'Generation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };
  
  const handleRetryGeneration = () => {
    if (lastGenerationParams) {
      handleGenerate(lastGenerationParams.prompt, lastGenerationParams.files);
    }
  };
  
  const handleCancelGeneration = () => {
    setGenerationError(null);
    setLastGenerationParams(null);
    // Note: Can't actually cancel in-flight request, but reset UI state
  };

  const handleDeleteClick = (rowId: string) => {
    setDeleteConfirmId(rowId);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmId) return;
    
    const rowId = deleteConfirmId;
    setDeleteConfirmId(null); // Close dialog immediately
    
    // Find the row data to store for undo
    const rowToDelete = items.find((item: any) => item.id === rowId);
    
    // Use mutate (non-blocking) instead of mutateAsync
    // Optimistic update will remove item from UI immediately
    deleteRow.mutate(rowId, {
      onSuccess: () => {
        // Store for undo (strip id for recreation)
        if (rowToDelete) {
          const { id, createdAt, updatedAt, createdBy, attachments, ...restoreData } = rowToDelete as any;
          pushToUndoHistory('delete', restoreData);
        }
        
        toast({
          title: 'Deleted',
          description: 'Row deleted successfully.',
        });
      },
      onError: (error) => {
        toast({
          title: 'Delete Failed',
          description: error instanceof Error ? error.message : 'Failed to delete row',
          variant: 'destructive',
        });
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleSaveRow = async (data: Record<string, unknown>) => {
    try {
      if (editingRowId) {
        // Store previous data for undo
        const previousRow = items.find((item: any) => item.id === editingRowId);
        
        // Update existing row
        await updateRow.mutateAsync({ rowId: editingRowId, data });
        
        // Push to undo history
        if (previousRow) {
          const { id, createdAt, updatedAt, createdBy, attachments, ...previousData } = previousRow as any;
          pushToUndoHistory('update', { rowId: editingRowId, previousData });
        }
        
        toast({
          title: 'Updated',
          description: 'Row updated successfully.',
        });
        setEditingRowId(null);
      } else {
        // Create new row
        const result = await createRow.mutateAsync(data);
        
        // Push to undo history with the new row's ID
        if (result?.data?.id) {
          pushToUndoHistory('create', { id: result.data.id });
        }
        
        toast({
          title: 'Created',
          description: 'Row created successfully.',
        });
        setShowAddDialog(false);
      }
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save row',
        variant: 'destructive',
      });
    }
  };

  const handleVersionRestore = async (version: Version) => {
    try {
      await restoreVersion.mutateAsync(version.id);
      // Update which version is now current
      setCurrentVersionId(version.id);
      toast({
        title: 'Version Switched',
        description: `Switched to ${version.name}`,
      });
    } catch (error) {
      toast({
        title: 'Switch Failed',
        description: error instanceof Error ? error.message : 'Failed to switch version',
        variant: 'destructive',
      });
    }
  };

  const handleAskAI = async (message: string): Promise<{ response: string; citations: never[] }> => {
    console.log('Asking AI:', message);
    
    // Use the generate mutation to actually create content
    try {
      await generate.mutateAsync({
        prompt: message,
        attachedFiles: [],
      });
      
      toast({
        title: "AI Generation Complete",
        description: `New ${activeView} have been generated based on your request.`,
      });
      
      setShowAskAI(false);
      return { response: `Generated new ${activeView} based on: ${message}`, citations: [] };
    } catch (error) {
      console.error('AI generation failed:', error);
      toast({
        title: "AI Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate content",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Add action to undo history
  const pushToUndoHistory = useCallback((action: string, data: any) => {
    undoHistoryRef.current.push({
      action,
      data,
      timestamp: Date.now(),
    });
    // Keep only last 20 actions
    if (undoHistoryRef.current.length > 20) {
      undoHistoryRef.current.shift();
    }
  }, []);
  
  const handleUndo = async () => {
    const lastAction = undoHistoryRef.current.pop();
    
    if (!lastAction) {
      toast({
        title: "Nothing to undo",
        description: "No actions to undo in this session",
      });
      return;
    }
    
    try {
      // Handle different undo actions
      if (lastAction.action === 'delete') {
        // Recreate the deleted row
        await createRow.mutateAsync(lastAction.data);
        toast({
          title: "Undo successful",
          description: "Deleted item has been restored",
        });
      } else if (lastAction.action === 'update') {
        // Restore previous values
        await updateRow.mutateAsync({
          rowId: lastAction.data.rowId,
          data: lastAction.data.previousData,
        });
        toast({
          title: "Undo successful",
          description: "Changes have been reverted",
        });
      } else if (lastAction.action === 'create') {
        // Delete the created row
        await deleteRow.mutateAsync(lastAction.data.id);
        toast({
          title: "Undo successful",
          description: "Created item has been removed",
        });
      }
      
      onUndo?.();
    } catch (error) {
      toast({
        title: "Undo failed",
        description: error instanceof Error ? error.message : "Failed to undo action",
        variant: "destructive",
      });
      // Put the action back on the stack
      undoHistoryRef.current.push(lastAction);
    }
  };

  const handleCopy = () => {
    // Copy content to clipboard
    const content = JSON.stringify(items, null, 2);
    navigator.clipboard.writeText(content);
    onCopy?.();
    toast({
      title: "Copied",
      description: "Content copied to clipboard",
    });
  };
  
  const handleOpenDetail = (rowId: string) => {
    // Update URL with item param for deep linking
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('item', rowId);
      return newParams;
    }, { replace: true });
    
    // All types now use inline detail view
    setSelectedRowForDetail(rowId);
  };
  
  // Close detail view and clear URL param
  const handleCloseDetail = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('item');
      newParams.delete('row'); // Also clear legacy param
      return newParams;
    }, { replace: true });
    
    setSelectedRowForDetail(null);
    setShowDetailModal(false);
  };
  
  const handleCopyRowLink = async (rowId: string) => {
    const baseUrl = window.location.origin;
    const permalink = `${baseUrl}/projects/${projectId}/discovery-${activeView}?row=${rowId}`;
    
    try {
      await navigator.clipboard.writeText(permalink);
      toast({ title: 'Link copied', description: 'Permalink copied to clipboard' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };
  
  const clearFilters = () => {
    setPriorityFilter('');
    setCategoryFilter('');
    setInfluenceFilter('');
    setThreatFilter('');
  };
  
  // Get selected row for detail modal
  const selectedRow = selectedRowForDetail 
    ? items.find((item: any) => item.id === selectedRowForDetail) 
    : null;

  // Render goals table - matching Figma design
  const renderGoalsTable = () => {
    const goals = items as DiscoveryGoal[];
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Goal</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Description</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Success Metrics</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Timeline</th>
              <th className="px-6 py-4 text-right text-sm font-normal text-gray-500 italic">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {goals.map((goal) => (
              <tr key={goal.id} className="hover:bg-gray-50 transition-colors">
                {/* Goal column with title + badges */}
                <td className="px-6 py-5">
                  <div>
                    <div className="font-semibold text-gray-900 mb-2">{goal.title}</div>
                    <div className="flex items-center gap-2">
                      {goal.category && (
                        <span className={cn(
                          "px-2.5 py-0.5 text-xs font-medium rounded-full",
                          goal.category === 'BUSINESS' && "bg-purple-100 text-purple-700",
                          goal.category === 'USER' && "bg-blue-100 text-blue-700",
                          goal.category === 'PRODUCT' && "bg-indigo-100 text-indigo-700",
                          goal.category === 'TECHNICAL' && "bg-slate-100 text-slate-700"
                        )}>
                          {goal.category.charAt(0) + goal.category.slice(1).toLowerCase()}
                        </span>
                      )}
                      {goal.priority && (
                        <span className={cn(
                          "px-2.5 py-0.5 text-xs font-medium rounded-full",
                          goal.priority === 'CRITICAL' && "bg-red-100 text-red-700",
                          goal.priority === 'HIGH' && "bg-orange-100 text-orange-700",
                          goal.priority === 'MEDIUM' && "bg-yellow-100 text-yellow-700",
                          goal.priority === 'LOW' && "bg-green-100 text-green-700"
                        )}>
                          {goal.priority.charAt(0) + goal.priority.slice(1).toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                {/* Description column */}
                <td className="px-6 py-5">
                  <div className="text-gray-600 max-w-xs leading-relaxed line-clamp-3">
                    {goal.description || '-'}
                  </div>
                </td>
                {/* Success Metrics column */}
                <td className="px-6 py-5">
                  <div className="text-gray-600 max-w-xs leading-relaxed line-clamp-3">
                    {goal.successMetrics || '-'}
                  </div>
                </td>
                {/* Timeline column */}
                <td className="px-6 py-5">
                  <div className="text-gray-700">{goal.timeline || '-'}</div>
                </td>
                {/* Actions column - arrow and trash icons */}
                <td className="px-6 py-5">
                  <div className="flex items-center justify-end gap-3">
                    <button 
                      onClick={() => handleOpenDetail(goal.id)}
                      className="p-1 text-gray-400 hover:text-brand-primary transition-colors"
                      title="View details"
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.16667 10H15.8333M15.8333 10L10 4.16667M15.8333 10L10 15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(goal.id)}
                      disabled={deleteRow.isPending}
                      className="p-1 text-red-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render interviews table - matching Figma design
  const renderInterviewsTable = () => {
    const interviews = items as DiscoveryInterview[];
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Stakeholder</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Date</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Key Insights</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Pain Points</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Influence</th>
              <th className="px-6 py-4 text-right text-sm font-normal text-gray-500 italic">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {interviews.map((interview) => (
              <tr key={interview.id} className="hover:bg-gray-50 transition-colors">
                {/* Stakeholder column - Name (bold), Position (purple), Department (gray) */}
                <td className="px-6 py-5 max-w-[200px]">
                  <div>
                    <div className="font-semibold text-gray-900 truncate">{interview.stakeholderName}</div>
                    {interview.stakeholderTitle && (
                      <div className="text-sm text-purple-500 mt-0.5 truncate">{interview.stakeholderTitle}</div>
                    )}
                    {interview.stakeholderOrg && (
                      <div className="text-sm text-gray-400 truncate">{interview.stakeholderOrg}</div>
                    )}
                  </div>
                </td>
                {/* Date column */}
                <td className="px-6 py-5">
                  <div className="text-gray-700">
                    {interview.interviewDate 
                      ? new Date(interview.interviewDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        }).replace(',', ',')
                      : '-'
                    }
                  </div>
                </td>
                {/* Key Insights column */}
                <td className="px-6 py-5">
                  <div className="text-gray-700 max-w-[200px] leading-relaxed line-clamp-3">
                    {interview.keyInsights || '-'}
                  </div>
                </td>
                {/* Pain Points column */}
                <td className="px-6 py-5">
                  <div className="text-gray-500 max-w-[180px] leading-relaxed line-clamp-3">
                    {interview.painPoints || '-'}
                  </div>
                </td>
                {/* Influence column */}
                <td className="px-6 py-5">
                  {interview.influence && (
                    <span className={cn(
                      "px-3 py-1 text-xs font-medium rounded-full",
                      interview.influence === 'HIGH' && "bg-purple-100 text-purple-700",
                      interview.influence === 'MEDIUM' && "bg-amber-100 text-amber-700",
                      interview.influence === 'LOW' && "bg-green-100 text-green-700"
                    )}>
                      {interview.influence.charAt(0) + interview.influence.slice(1).toLowerCase()}
                    </span>
                  )}
                </td>
                {/* Actions column - arrow and trash icons */}
                <td className="px-6 py-5">
                  <div className="flex items-center justify-end gap-3">
                    <button 
                      onClick={() => handleOpenDetail(interview.id)}
                      className="p-1 text-gray-400 hover:text-brand-primary transition-colors"
                      title="View details"
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.16667 10H15.8333M15.8333 10L10 4.16667M15.8333 10L10 15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(interview.id)}
                      disabled={deleteRow.isPending}
                      className="p-1 text-red-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render competitors table
  const renderCompetitorsTable = () => {
    const competitors = items as DiscoveryCompetitor[];
    
    // Category badge colors matching design
    const categoryColors: Record<string, string> = {
      DIRECT: 'bg-blue-100 text-blue-700',
      INDIRECT: 'bg-amber-100 text-amber-700',
      SUBSTITUTE: 'bg-lime-100 text-lime-700',
    };
    
    // Threat level badge colors matching design
    const threatBadgeColors: Record<string, string> = {
      HIGH: 'bg-red-50 text-red-600',
      MEDIUM: 'bg-amber-100 text-amber-700',
      LOW: 'bg-green-50 text-green-600',
    };
    
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Competitor</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Market Position</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Strengths</th>
              <th className="px-6 py-4 text-left text-sm font-normal text-gray-500 italic">Market Share</th>
              <th className="px-6 py-4 text-right text-sm font-normal text-gray-500 italic">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {competitors.map((competitor) => (
              <tr key={competitor.id} className="hover:bg-gray-50 transition-colors">
                {/* Competitor column: Name + Category badge + Threat badge */}
                <td className="px-6 py-5">
                  <div className="font-semibold text-gray-900 mb-2">{competitor.name}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {competitor.category && (
                      <span className={cn(
                        "px-2.5 py-1 text-xs font-medium rounded-full",
                        categoryColors[competitor.category] || 'bg-gray-100 text-gray-700'
                      )}>
                        {competitor.category === 'DIRECT' ? 'Direct' : 
                         competitor.category === 'INDIRECT' ? 'Indirect' : 
                         competitor.category === 'SUBSTITUTE' ? 'Substitute' : competitor.category}
                      </span>
                    )}
                    {competitor.threatLevel && (
                      <span className={cn(
                        "px-2.5 py-1 text-xs font-medium rounded-full",
                        threatBadgeColors[competitor.threatLevel] || 'bg-gray-100 text-gray-700'
                      )}>
                        {competitor.threatLevel === 'HIGH' ? 'High' : 
                         competitor.threatLevel === 'MEDIUM' ? 'Medium' : 
                         competitor.threatLevel === 'LOW' ? 'Low' : competitor.threatLevel}
                      </span>
                    )}
                  </div>
                </td>
                {/* Market Position column */}
                <td className="px-6 py-5">
                  <div className="text-gray-700 max-w-md line-clamp-3">{competitor.marketPosition || '-'}</div>
                </td>
                {/* Strengths column */}
                <td className="px-6 py-5">
                  <div className="text-gray-600 text-sm max-w-xs line-clamp-3">{competitor.strengths || '-'}</div>
                </td>
                {/* Market Share column */}
                <td className="px-6 py-5">
                  <div className="text-gray-900 font-medium">{competitor.marketShare || '-'}</div>
                </td>
                {/* Actions column - arrow and trash icons */}
                <td className="px-6 py-5">
                  <div className="flex items-center justify-end gap-3">
                    <button 
                      onClick={() => handleOpenDetail(competitor.id)}
                      className="p-1 text-gray-400 hover:text-brand-primary transition-colors"
                      title="View details"
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.16667 10H15.8333M15.8333 10L10 4.16667M15.8333 10L10 15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(competitor.id)}
                      disabled={deleteRow.isPending}
                      className="p-1 text-red-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderContent = () => {
    if (activeView === 'goals') return renderGoalsTable();
    if (activeView === 'interviews') return renderInterviewsTable();
    if (activeView === 'competitors') return renderCompetitorsTable();
    return null;
  };

  if (!token) {
    return (
      <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h2>
        <p className="text-gray-600 mb-4">Please log in to view discovery content.</p>
        <a
          href="/login"
          className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90"
        >
          Go to Login
        </a>
      </div>
    );
  }

  if (isLoading) {
    // Render skeleton table matching the actual table structure
    return (
      <div className="flex-1 bg-gray-50 overflow-y-auto">
        <div className="container">
        {/* Page Sub-Header Skeleton */}
        <div className="bg-white px-8 py-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-48" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-[42px] w-24 rounded-full" />
              <Skeleton className="h-[42px] w-28 rounded-full" />
              <Skeleton className="h-[42px] w-24 rounded-full" />
            </div>
          </div>
        </div>
        
        {/* Table Skeleton */}
        <div className="px-8 py-6">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  {activeView === 'goals' && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-32" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-28" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-20" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                  {activeView === 'interviews' && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-16" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-28" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-20" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                  {activeView === 'competitors' && (
                    <>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-32" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-24" /></th>
                      <th className="px-6 py-4 text-left"><Skeleton className="h-4 w-28" /></th>
                      <th className="px-6 py-4 text-right"><Skeleton className="h-4 w-16" /></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    {activeView === 'goals' && (
                      <>
                        <td className="px-6 py-5"><Skeleton className="h-5 w-48" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-full max-w-[300px]" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-6 w-20 rounded-full" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-6 w-16 rounded-full" /></td>
                        <td className="px-6 py-5"><div className="flex justify-end gap-3"><Skeleton className="h-5 w-5" /><Skeleton className="h-5 w-5" /></div></td>
                      </>
                    )}
                    {activeView === 'interviews' && (
                      <>
                        <td className="px-6 py-5"><div className="space-y-1"><Skeleton className="h-5 w-32" /><Skeleton className="h-4 w-24" /></div></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-20" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-full max-w-[200px]" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-full max-w-[180px]" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-6 w-16 rounded-full" /></td>
                        <td className="px-6 py-5"><div className="flex justify-end gap-3"><Skeleton className="h-5 w-5" /><Skeleton className="h-5 w-5" /></div></td>
                      </>
                    )}
                    {activeView === 'competitors' && (
                      <>
                        <td className="px-6 py-5"><div className="space-y-2"><Skeleton className="h-5 w-32" /><div className="flex gap-2"><Skeleton className="h-5 w-16 rounded-full" /><Skeleton className="h-5 w-20 rounded-full" /></div></div></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-full max-w-md" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-full max-w-xs" /></td>
                        <td className="px-6 py-5"><Skeleton className="h-4 w-16" /></td>
                        <td className="px-6 py-5"><div className="flex justify-end gap-3"><Skeleton className="h-5 w-5" /><Skeleton className="h-5 w-5" /></div></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    );
  }

  if (isError) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const isAuthError = errorMessage.toLowerCase().includes('token') || errorMessage.toLowerCase().includes('auth');
    
    return (
      <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-2">Failed to Load</h2>
        <p className="text-gray-600 mb-4">
          {isAuthError ? 'Your session may have expired.' : 'There was an error loading the discovery data.'}
        </p>
        {isAuthError ? (
          <a
            href="/login"
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90"
          >
            Log In Again
          </a>
        ) : (
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90"
          >
            Refresh Page
          </button>
        )}
        <p className="text-sm text-gray-500 mt-4">Error: {errorMessage}</p>
      </div>
    );
  }

  // Handle save from goal detail view
  const handleSaveGoal = async (data: Partial<DiscoveryGoal>) => {
    if (!selectedRowForDetail) return;
    
    try {
      await updateRow.mutateAsync({ rowId: selectedRowForDetail, data });
      toast({
        title: 'Saved',
        description: 'Goal updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save goal',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Handle save from interview detail view
  const handleSaveInterview = async (data: Partial<DiscoveryInterview>) => {
    if (!selectedRowForDetail) return;
    
    try {
      await updateRow.mutateAsync({ rowId: selectedRowForDetail, data });
      toast({
        title: 'Saved',
        description: 'Interview updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save interview',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Handle save from competitor detail view
  const handleSaveCompetitor = async (data: Partial<DiscoveryCompetitor>) => {
    if (!selectedRowForDetail) return;
    
    try {
      await updateRow.mutateAsync({ rowId: selectedRowForDetail, data });
      toast({
        title: 'Saved',
        description: 'Competitor updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save competitor',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // If a goal is selected (goals view), render the detail view
  if (activeView === 'goals' && selectedRowForDetail && selectedRow) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <GoalDetailView
          goal={selectedRow as DiscoveryGoal}
          projectName={projectName}
          onBack={handleCloseDetail}
          onSave={handleSaveGoal}
          onAskAI={handleAskAI}
          onCopy={handleCopy}
        />
      </div>
    );
  }

  // If an interview is selected (interviews view), render the detail view
  if (activeView === 'interviews' && selectedRowForDetail && selectedRow) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <InterviewDetailView
          interview={selectedRow as DiscoveryInterview}
          projectName={projectName}
          onBack={handleCloseDetail}
          onSave={handleSaveInterview}
          onAskAI={handleAskAI}
          onCopy={handleCopy}
        />
      </div>
    );
  }

  // If a competitor is selected (competitors view), render the detail view
  if (activeView === 'competitors' && selectedRowForDetail && selectedRow) {
    return (
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <CompetitorDetailView
          competitor={selectedRow as DiscoveryCompetitor}
          projectName={projectName}
          onBack={handleCloseDetail}
          onSave={handleSaveCompetitor}
          onAskAI={handleAskAI}
          onCopy={handleCopy}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER - Does NOT scroll */}
      <div className="shrink-0 bg-white px-8 py-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          {/* Title */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
              {config.title}
            </h1>
            <p className="text-base text-gray-500 mt-1">
              {config.subtitle} {projectName}
            </p>
          </div>

          {/* Action Buttons - Matching Figma Design */}
          <div className="flex items-center gap-2">
            {/* Add Button - Only for Goals and Interviews */}
            {(activeView === 'goals' || activeView === 'interviews') && (
              <button
                onClick={() => setShowAddDialog(true)}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Add</span>
              </button>
            )}

            {/* Ask AI Button */}
            <button
              onClick={() => setShowAskAI(!showAskAI)}
              className="h-[42px] px-4 flex items-center gap-2 rounded-full transition-colors text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Ask AI</span>
            </button>

            {/* Versions Dropdown */}
            <VersionsDropdown
              versions={versionOptions}
              onVersionSelect={handleVersionRestore}
            />

            {/* Copy Button */}
            <button 
              onClick={handleCopy}
              className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700"
            >
              <Copy className="w-4 h-4" />
              <span className="text-sm font-medium">Copy</span>
            </button>
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="container">
        {/* Ask AI Bar */}
        {showAskAI && (
          <div className="px-8 pt-6">
            <AskAIBar
              isOpen={showAskAI}
              onClose={() => setShowAskAI(false)}
              onSend={handleAskAI}
              placeholder={config.askAIPlaceholder}
            />
          </div>
        )}

        {/* Content Area */}
        {hasContent ? (
          <div className="px-8 py-6">
            {/* Stats Bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>
                  {items.length} {activeView}
                  {hasActiveFilters && items.length !== allItems.length && (
                    <span className="text-gray-400 ml-1">(filtered from {allItems.length})</span>
                  )}
                </span>
              </div>
            </div>
            {renderContent()}
          </div>
        ) : (
          /* Empty State Generator */
          <div className="px-8 py-8">
            <div className="max-w-[585px] mx-auto">
              {/* Icon and Title */}
              <div className="text-center mb-8">
                {/* Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-[#e0e7ff] rounded-[14px] flex items-center justify-center">
                  <Icon className="w-10 h-10 text-brand-primary" />
                </div>

                <h2 className="text-lg font-medium text-gray-900 mb-3">
                  {config.emptyTitle}
                </h2>

                <p className="text-base text-gray-600 max-w-[448px] mx-auto leading-6">
                  {config.emptyDescription}
                </p>
              </div>

              {/* Input Area with File Attachment */}
              <ArtifactGeneratorInput
                key={activeView}
                placeholder={config.placeholder}
                onGenerate={handleGenerate}
                isGenerating={generate.isPending}
              />

              {/* Footer Text */}
              <p className="text-center text-base text-gray-500 mt-6">
                AI will analyze your input and generate structured {activeView}
              </p>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Edit Row Dialog */}
      <EditRowDialog
        isOpen={!!editingRowId}
        onClose={() => setEditingRowId(null)}
        onSave={handleSaveRow}
        type={activeView}
        row={editingRow as DiscoveryGoal | DiscoveryInterview | DiscoveryCompetitor | null}
        isLoading={updateRow.isPending}
      />

      {/* Add Row Dialog */}
      <EditRowDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSave={handleSaveRow}
        type={activeView}
        row={null}
        isLoading={createRow.isPending}
      />

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        versions={versions || []}
        onRestore={async (versionId) => {
          await restoreVersion.mutateAsync(versionId);
        }}
        isRestoring={restoreVersion.isPending}
        type={activeView}
      />
      
      {/* Row Detail Modal */}
      <RowDetailModal
        isOpen={showDetailModal}
        onClose={handleCloseDetail}
        type={activeView}
        row={selectedRow as DiscoveryGoal | DiscoveryInterview | DiscoveryCompetitor | null}
        projectId={projectId}
        onEdit={() => {
          handleCloseDetail();
          if (selectedRowForDetail) {
            setEditingRowId(selectedRowForDetail);
          }
        }}
      />

      {/* AI Generation Progress Modal */}
      <AIGenerationProgress
        isGenerating={generate.isPending}
        error={generationError}
        onRetry={handleRetryGeneration}
        onCancel={handleCancelGeneration}
        artifactType={activeView}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (() => {
        const itemToDelete = items.find((item: any) => item.id === deleteConfirmId);
        const itemName = activeView === 'goals' 
          ? (itemToDelete as DiscoveryGoal)?.title 
          : activeView === 'interviews'
          ? (itemToDelete as DiscoveryInterview)?.stakeholderName
          : (itemToDelete as DiscoveryCompetitor)?.name;
        
        return (
          <ConfirmDialog
            isOpen={!!deleteConfirmId}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Item"
            description={
              <span>
                Are you sure you want to delete <strong>{itemName || 'this item'}</strong>? This action cannot be undone.
              </span>
            }
            confirmText="Delete"
            cancelText="Cancel"
            variant="danger"
            isLoading={false}
          />
        );
      })()}
    </div>
  );
}

export type { DiscoveryType };
